// Phase 3 — Action-inbox Dashboard. Role-aware "what needs my attention"
// landing page. Replaces the older stats-tiles + pie-chart Dashboard so
// every user lands directly on the things they can act on.
//
// Sections rendered top-to-bottom:
//   1. Slim hero (team badge + greeting)
//   2. THIS WEEK — next upcoming match + next practice as compact cards
//   3. YOUR ACTIONS — player actions (set availability for next match,
//      update goal progress, mark practice attendance, volunteer for drinks)
//   4. CAPTAIN'S DESK — only for elevated users: open drafts to publish,
//      matches missing a squad, players who haven't responded
//   5. STATS STRIP — the small at-a-glance counters from the old design,
//      pushed below the actionable rows
//   6. LATEST ANNOUNCEMENTS — top 4 announcements as compact cards
//
// Every action card has a one-click button: "Set Available" doesn't open
// another page, it hits the API and toasts back.

import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import {
  CalendarDays, Clock, MapPin, Trophy, Users, Wine, Target,
  Check, AlertTriangle, ChevronRight, Megaphone, Inbox, Sparkles,
  Send, FileEdit, Award, Zap, Sigma, Hand, Shield, BarChart3, Activity
} from "lucide-react";
import { Panel, Card } from "../components/primitives";
import { useActiveTeam } from "../components/ActiveTeam";
import { useToast } from "../components/Toast";
import { useVersion } from "../components/Version";
import { api } from "../api";
import DashboardMobile from "./DashboardMobile";

const ELEVATED = ["ADMIN","PRESIDENT","VICE_PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"];
const STATUS_OPTIONS = [
  { value: "Available",     label: "Available",   color: "var(--green)" },
  { value: "Tentative",     label: "Tentative",   color: "var(--orange)" },
  { value: "Not Available", label: "Unavailable", color: "var(--red)" }
];

function prettyDay(iso) {
  if (!iso) return "TBD";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export default function Dashboard(props) {
  // V4 swaps to the dedicated mobile layout. Same data shape, same
  // navigate prop — different chrome and card density.
  const { version } = useVersion();
  if (version === "v4") return <DashboardMobile {...props}/>;
  return <DashboardDesktop {...props}/>;
}

function DashboardDesktop({ state, user, navigate, reload }) {
  const toast = useToast();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const elevated = user?.roles?.some(r => ELEVATED.includes(r));
  const today = new Date().toISOString().slice(0, 10);

  // ---------- SCOPED DATA ----------
  const scopedMatches = useMemo(
    () => isAllTeams ? state.matches : state.matches.filter(m => m.teamId === activeTeamId),
    [state.matches, isAllTeams, activeTeamId]
  );
  const scopedMembers = useMemo(() => {
    if (isAllTeams) return state.members;
    const ids = new Set(activeTeam?.players || []);
    return state.members.filter(m => ids.has(m.id));
  }, [state.members, activeTeam, isAllTeams]);
  const scopedAvailability = useMemo(() => {
    const matchIds = new Set(scopedMatches.map(m => m.id));
    return state.availability.filter(a => matchIds.has(a.matchId));
  }, [state.availability, scopedMatches]);
  const scopedSquads = useMemo(() => {
    if (isAllTeams) return state.squads;
    const matchByTeam = new Map(state.matches.map(m => [m.id, m.teamId]));
    return state.squads.filter(s => matchByTeam.get(s.matchId) === activeTeamId);
  }, [state.squads, state.matches, isAllTeams, activeTeamId]);

  // ---------- THIS WEEK ----------
  const upcoming = useMemo(
    () => scopedMatches.filter(m => m.date >= today)
      .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)),
    [scopedMatches, today]
  );
  const nextMatch = upcoming[0] || null;
  const next2Matches = upcoming.slice(0, 2);

  // ---------- PLAYER ACTIONS ----------
  // Pending availability: next 2 upcoming matches where the user is on the
  // roster and hasn't responded yet.
  const pendingAvailability = useMemo(() => {
    if (!user?.id) return [];
    return next2Matches.filter(m => {
      const team = state.teams.find(t => t.id === m.teamId);
      if (!team?.players?.includes(user.id)) return false;
      return !state.availability.some(a => a.matchId === m.id && a.userId === user.id);
    });
  }, [next2Matches, state.teams, state.availability, user?.id]);

  async function quickSetAvailability(matchId, status) {
    try {
      await api.setAvailability(matchId, user.id, { status, comments: "" });
      await reload();
      toast.success(`Marked ${status.toLowerCase()}.`);
    } catch (err) { toast.error(err.message || "Couldn't update."); }
  }

  async function quickVolunteerDrinks(matchId) {
    try {
      await api.setAvailability(matchId, user.id, { volunteerDrinks: true });
      await reload();
      toast.success("Thanks for volunteering for drinks!");
    } catch (err) { toast.error(err.message || "Couldn't update."); }
  }

  // Drinks ask: for the next match, has anyone volunteered? If not and the
  // user hasn't volunteered for the season yet, surface the prompt.
  const drinksDoneByUser = useMemo(() => {
    const s = new Set();
    for (const a of (state.availability || [])) if (a.volunteerDrinks) s.add(a.userId);
    return s;
  }, [state.availability]);
  const drinksAskMatch = useMemo(() => {
    if (!nextMatch || !user?.id) return null;
    if (drinksDoneByUser.has(user.id)) return null;
    const team = state.teams.find(t => t.id === nextMatch.teamId);
    if (!team?.players?.includes(user.id)) return null;
    const hasVol = state.availability.some(a => a.matchId === nextMatch.id && a.volunteerDrinks);
    return hasVol ? null : nextMatch;
  }, [nextMatch, drinksDoneByUser, user?.id, state.teams, state.availability]);

  // ---------- CAPTAIN'S DESK ----------
  // Matches in the next 21 days that don't yet have a squad at all.
  const matchesNeedingSquad = useMemo(() => {
    if (!elevated) return [];
    const horizon = new Date(); horizon.setDate(horizon.getDate() + 21);
    const horIso = horizon.toISOString().slice(0, 10);
    const haveSquad = new Set(scopedSquads.map(s => s.matchId));
    return scopedMatches.filter(m =>
      m.date >= today && m.date <= horIso && !haveSquad.has(m.id)
    ).slice(0, 5);
  }, [elevated, scopedMatches, scopedSquads, today]);

  // Drafted-but-not-yet-published squads for matches still ahead.
  const draftsToPublish = useMemo(() => {
    if (!elevated) return [];
    return scopedSquads
      .filter(s => !s.isPublished)
      .map(s => ({ squad: s, match: scopedMatches.find(m => m.id === s.matchId) }))
      .filter(x => x.match && x.match.date >= today)
      .slice(0, 5);
  }, [elevated, scopedSquads, scopedMatches, today]);

  // Players who haven't responded for the next match.
  const noResponsePlayers = useMemo(() => {
    if (!elevated || !nextMatch) return { match: null, players: [] };
    const team = state.teams.find(t => t.id === nextMatch.teamId);
    if (!team) return { match: null, players: [] };
    const responded = new Set(
      state.availability.filter(a => a.matchId === nextMatch.id).map(a => a.userId)
    );
    const players = (team.players || [])
      .filter(uid => !responded.has(uid))
      .map(uid => state.members.find(m => m.id === uid))
      .filter(Boolean);
    return { match: nextMatch, players };
  }, [elevated, nextMatch, state.teams, state.availability, state.members]);

  // ---------- STATS STRIP ----------
  const availabilityCounts = useMemo(() => {
    if (!nextMatch) return { a: 0, t: 0, n: 0, none: 0 };
    const team = state.teams.find(t => t.id === nextMatch.teamId);
    const total = (team?.players || []).length;
    const rows = state.availability.filter(a => a.matchId === nextMatch.id);
    const a = rows.filter(r => r.status === "Available").length;
    const t = rows.filter(r => r.status === "Tentative").length;
    const n = rows.filter(r => r.status === "Not Available").length;
    return { a, t, n, none: total - a - t - n };
  }, [nextMatch, state.teams, state.availability]);

  const ann = useMemo(
    () => [...state.announcements]
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))
      .slice(0, 4),
    [state.announcements]
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="stack">
      {/* ───── SLIM HERO ───── */}
      <section className="hero">
        <div>
          <span className="eyebrow">Your hub · {isAllTeams ? "All Teams" : (activeTeam?.name || "Pitt Gabruz")}</span>
          <h2>Hi {user.name?.split(" ")[0] || user.name}</h2>
          <p style={{margin:"6px 0 0"}}>
            {pendingAvailability.length > 0
              ? `You have ${pendingAvailability.length} match${pendingAvailability.length > 1 ? "es" : ""} that need your availability.`
              : (nextMatch
                  ? `Next up — ${nextMatch.opponent} on ${prettyDay(nextMatch.date)}.`
                  : "No matches on the books yet.")}
          </p>
        </div>
        <div className="hero-badge">
          <img src="/logo.png" onError={e => e.currentTarget.style.display = "none"} />
          <span>{isAllTeams ? "All Teams" : (activeTeam?.name || "Pitt Gabruz")}</span>
        </div>
      </section>

      {/* ───── MY STATS (Rowdys players only) ───── */}
      {activeTeam?.code === "ROWDYS_CRICKET" && (
        <MyCricketStats user={user} team={activeTeam} />
      )}

      {/* ───── YOUR ACTIONS ───── */}
      <Panel title={<><Inbox size={18}/> Your actions</>}>
        {pendingAvailability.length === 0 && !drinksAskMatch && (
          <div style={{padding:"18px 0", textAlign:"center", color:"var(--muted)"}}>
            <Check size={32} style={{color:"var(--green)", marginBottom:6}}/>
            <p>You're all caught up. ✨</p>
          </div>
        )}

        {pendingAvailability.map(m => (
          <ActionRow
            key={`av-${m.id}`}
            icon={<AlertTriangle size={18} style={{color:"var(--orange)"}}/>}
            title={`Set availability — vs ${m.opponent}`}
            sub={`${prettyDay(m.date)} · ${m.time || "TBD"} · ${m.venue || "TBD"}`}
          >
            <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className="dark"
                  style={{padding:"6px 12px", fontSize:12, borderColor: opt.color, color: opt.color}}
                  onClick={() => quickSetAvailability(m.id, opt.value)}
                >{opt.label}</button>
              ))}
            </div>
          </ActionRow>
        ))}

        {drinksAskMatch && (
          <ActionRow
            icon={<Wine size={18} style={{color:"var(--accent)"}}/>}
            title={`Volunteer for drinks — vs ${drinksAskMatch.opponent}`}
            sub={`${prettyDay(drinksAskMatch.date)} · No one has signed up yet. Take a turn this season.`}
          >
            <button className="btn" onClick={() => quickVolunteerDrinks(drinksAskMatch.id)}>
              <Wine size={14}/> Volunteer
            </button>
          </ActionRow>
        )}
      </Panel>

      {/* ───── CAPTAIN'S DESK ───── */}
      {elevated && (matchesNeedingSquad.length + draftsToPublish.length + noResponsePlayers.players.length > 0) && (
        <Panel title={<><Sparkles size={18}/> Captain's desk</>}>
          {matchesNeedingSquad.map(m => (
            <ActionRow
              key={`needSquad-${m.id}`}
              icon={<Trophy size={18} style={{color:"var(--orange)"}}/>}
              title={`Build squad — vs ${m.opponent}`}
              sub={`${prettyDay(m.date)} · ${m.time || "TBD"} · ${m.sport} · no squad yet`}
            >
              <button className="dark" onClick={() => navigate?.("squad")}>
                <Trophy size={14}/> Open Squads
              </button>
            </ActionRow>
          ))}
          {draftsToPublish.map(({ squad, match }) => (
            <ActionRow
              key={`draft-${squad.id}`}
              icon={<FileEdit size={18} style={{color:"var(--blue)"}}/>}
              title={`Draft ready — vs ${match.opponent}`}
              sub={`${prettyDay(match.date)} · ${squad.players?.length || 0} starters drafted · publish to announce`}
            >
              <button className="dark" onClick={() => navigate?.("squad")}>
                <Send size={14}/> Publish
              </button>
            </ActionRow>
          ))}
          {noResponsePlayers.match && noResponsePlayers.players.length > 0 && (
            <ActionRow
              icon={<Users size={18} style={{color:"var(--muted)"}}/>}
              title={`${noResponsePlayers.players.length} haven't responded`}
              sub={`For ${noResponsePlayers.match.opponent} on ${prettyDay(noResponsePlayers.match.date)} — ${noResponsePlayers.players.slice(0, 3).map(p => p.name).join(", ")}${noResponsePlayers.players.length > 3 ? `, +${noResponsePlayers.players.length - 3} more` : ""}`}
            >
              <button className="dark" onClick={() => navigate?.("availability")}>
                <CalendarDays size={14}/> Open Availability
              </button>
            </ActionRow>
          )}
        </Panel>
      )}

      {/* ───── THIS WEEK ───── */}
      <div className="grid2">
        {/* Next match */}
        <Panel title={<><CalendarDays size={18}/> Next match</>}>
          {nextMatch ? (
            <NextMatchCard
              match={nextMatch}
              counts={availabilityCounts}
              navigate={navigate}
            />
          ) : (
            <p style={{color:"var(--muted)", textAlign:"center", padding:"20px 0"}}>
              No matches scheduled. Add one in Schedules.
            </p>
          )}
        </Panel>

        {/* Latest announcements */}
        <Panel title={<><Megaphone size={18}/> Latest announcements</>}>
          {ann.length === 0 && (
            <p style={{color:"var(--muted)", textAlign:"center", padding:"20px 0"}}>
              Nothing posted yet.
            </p>
          )}
          {ann.map(a => (
            <div key={a.id} className="row" style={{display:"block", marginBottom:8}}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10}}>
                <strong style={{flex:1}}>{a.title}</strong>
                <span className="tag" style={{flexShrink:0}}>{a.priority}</span>
              </div>
              <span style={{marginTop:4, display:"block"}}>{a.body?.slice(0, 140)}{(a.body?.length || 0) > 140 ? "…" : ""}</span>
              <small style={{color:"var(--muted)", marginTop:4, display:"block"}}>
                Posted by {a.postedBy}
              </small>
            </div>
          ))}
        </Panel>
      </div>

      {/* ───── STATS STRIP (compact, less prominent than before) ───── */}
      <Panel title={<><Trophy size={18}/> At a glance — {isAllTeams ? "all teams" : activeTeam?.name}</>}>
        <div className="cardgrid">
          <MiniStat label={isAllTeams ? "Members" : "On this team"} value={scopedMembers.length} />
          <MiniStat label="Upcoming matches" value={upcoming.length} />
          <MiniStat label="Squads in flight" value={scopedSquads.length} />
          <MiniStat label="Available next match" value={`${availabilityCounts.a}/${(activeTeam?.players?.length || 0)}`} />
        </div>
      </Panel>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function ActionRow({ icon, title, sub, children }) {
  return (
    <div className="row" style={{display:"flex", alignItems:"center", gap:14, flexWrap:"wrap"}}>
      <div style={{flexShrink:0}}>{icon}</div>
      <div style={{flex:1, minWidth:200}}>
        <strong>{title}</strong>
        <span>{sub}</span>
      </div>
      <div style={{flexShrink:0}}>{children}</div>
    </div>
  );
}

function NextMatchCard({ match, counts, navigate }) {
  return (
    <div style={{display:"grid", gap:10}}>
      <div>
        <h3 style={{margin:"0 0 4px"}}>vs {match.opponent}</h3>
        <div style={{display:"flex", flexWrap:"wrap", gap:14, color:"var(--muted)", fontSize:14}}>
          <span><CalendarDays size={14} style={{verticalAlign:"middle"}}/> {prettyDay(match.date)}</span>
          {match.time && <span><Clock size={14} style={{verticalAlign:"middle"}}/> {match.time}</span>}
          {match.venue && <span><MapPin size={14} style={{verticalAlign:"middle"}}/> {match.venue}</span>}
        </div>
      </div>

      {/* Availability summary */}
      <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
        <span className="tag" style={{color:"var(--green)", borderColor:"var(--green)"}}>{counts.a} Available</span>
        <span className="tag" style={{color:"var(--orange)", borderColor:"var(--orange)"}}>{counts.t} Tentative</span>
        <span className="tag" style={{color:"var(--red)", borderColor:"var(--red)"}}>{counts.n} Unavailable</span>
        <span className="tag" style={{color:"var(--muted)", borderColor:"var(--muted)"}}>{counts.none} No response</span>
      </div>

      <div style={{display:"flex", gap:6, flexWrap:"wrap", marginTop:4}}>
        <button className="dark" onClick={() => navigate?.("availability")}>
          <CalendarDays size={14}/> Availability
        </button>
        <button className="dark" onClick={() => navigate?.("squad")}>
          <Trophy size={14}/> Squad
        </button>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="card" style={{padding:14, textAlign:"center"}}>
      <h3 style={{margin:0, fontSize:28, color:"var(--accent)"}}>{value}</h3>
      <p style={{margin:"4px 0 0", color:"var(--muted)", fontSize:12, textTransform:"uppercase", letterSpacing:"0.1em"}}>
        {label}
      </p>
    </div>
  );
}

// ============================================================================
// MyCricketStats — fancy career-stats panel for Rowdys Cricket Club players.
// Renders on the dashboard when the active team is ROWDYS_CRICKET. Fetches
// the logged-in user's stats + the admin-defined custom-field definitions,
// stacks them into colour-coded cards, and highlights derived averages.
// ============================================================================
const HEADLINE_FIELDS = [
  { key: "matchesPlayed", label: "Matches",        icon: Trophy,  accent: "var(--gold)" },
  { key: "runsScored",    label: "Runs",           icon: Target,  accent: "#5ce1e6" },
  { key: "wicketsTaken",  label: "Wickets",        icon: Shield,  accent: "#ff8a2a" },
  { key: "catchesTaken",  label: "Catches",        icon: Hand,    accent: "#c897ff" },
  { key: "maidenOvers",   label: "Maiden Overs",   icon: Activity,accent: "#ffd76a" },
  { key: "centuries",     label: "100's",          icon: Award,   accent: "#ffd76a" },
  { key: "sixes",         label: "6's",            icon: Zap,     accent: "#5ce1e6" },
  { key: "fours",         label: "4's",            icon: Sigma,   accent: "#c897ff" }
];

function customIcon(name) {
  return Icons[name] || BarChart3;
}

function MyCricketStats({ user, team }) {
  const [stats, setStats]   = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.getPlayerStats(user.id, team.id),
      api.listCricketStatFields()
    ])
      .then(([row, fdefs]) => {
        if (cancelled) return;
        setStats(row);
        setFields(fdefs);
      })
      .catch(() => { /* silent — dashboard tile not critical */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.id, team.id]);

  if (loading) {
    return (
      <section className="myStats">
        <div className="myStats-head">
          <div>
            <small>MY CAREER</small>
            <h2>{user.name}</h2>
            <em>{team.name}</em>
          </div>
        </div>
        <p style={{color:"var(--muted)", textAlign:"center", padding:20}}>Loading stats…</p>
      </section>
    );
  }

  const matches = stats?.matchesPlayed || 0;
  const runs    = stats?.runsScored    || 0;
  const wickets = stats?.wicketsTaken  || 0;
  const battingAvg     = matches > 0 ? (runs    / matches).toFixed(1)  : "—";
  const wicketsPerGame = matches > 0 ? (wickets / matches).toFixed(2) : "—";
  const boundaryHits   = (stats?.fours || 0) + (stats?.sixes || 0);

  return (
    <section className="myStats">
      <div className="myStats-head">
        <div className="myStats-avatar">
          <Sparkles size={26}/>
        </div>
        <div className="myStats-headText">
          <small>MY CAREER · {team.name?.toUpperCase()}</small>
          <h2>{user.name}</h2>
          <em>{(user.roles || []).filter(r => r !== "MEMBER").map(r => r.replace("_"," ")).join(" · ")}</em>
        </div>
        <div className="myStats-headAvg">
          <div className="myStats-avgCard">
            <span>BATTING AVG</span>
            <b>{battingAvg}</b>
          </div>
          <div className="myStats-avgCard">
            <span>WKTS / MATCH</span>
            <b>{wicketsPerGame}</b>
          </div>
        </div>
      </div>

      <div className="myStats-grid">
        {HEADLINE_FIELDS.map(f => {
          const Icon = f.icon;
          return (
            <div className="myStats-card" key={f.key} style={{ "--accent": f.accent }}>
              <div className="myStats-card-icon"><Icon size={20}/></div>
              <div className="myStats-card-num">{stats?.[f.key] ?? 0}</div>
              <div className="myStats-card-lbl">{f.label}</div>
            </div>
          );
        })}
        {/* Custom admin-defined fields render with the same card shape */}
        {fields.map(f => {
          const Icon = customIcon(f.icon);
          return (
            <div className="myStats-card" key={`custom-${f.id}`} style={{ "--accent": f.accent }}>
              <div className="myStats-card-icon"><Icon size={20}/></div>
              <div className="myStats-card-num">{stats?.custom?.[f.fieldKey] ?? 0}</div>
              <div className="myStats-card-lbl">{f.label}</div>
            </div>
          );
        })}
      </div>

      {/* Highlight ribbon */}
      <div className="myStats-ribbon">
        <span><Trophy size={13}/> Boundaries: <b>{boundaryHits}</b></span>
        <span><Award size={13}/> 100's: <b>{stats?.centuries || 0}</b></span>
        <span><Hand size={13}/> Catches: <b>{stats?.catchesTaken || 0}</b></span>
      </div>
    </section>
  );
}
